import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./EstimateTemplate.module.scss";
import classNames from "classnames/bind";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import { handleGetEstimateTemplates } from "~/thunks/estimate-setting/estimateSettingThunk";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import { PlusIcon } from "~/assets/icons";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import IntroductionTemplate from "./create-template/introduction-template/IntroductionTemplate";
import DetailsTemplate from "./create-template/details-template/DetailsTemplate";
import SigningUpgrades from "./create-template/signing-upgrades/SigningUpgrades";
import CustomPageTextTemplate from "./create-template/custom-page-text-template/CustomPageTextTemplate";
import EmailTemplate from "./create-template/email-template/EmailTemplate";

dayjs.extend(relativeTime);

export interface CreateTemplateCommonProps {
    templateName?: string;
    templateType?: string;
    location?: string;
    onCancel?: () => void;
}

const TEMPLATE_TYPE_COMPONENT_MAP: Record<string, React.ComponentType<CreateTemplateCommonProps>> = {
    'Introduction Template': IntroductionTemplate,
    'Estimate Details Template': DetailsTemplate,
    'Signing & Upgrades': SigningUpgrades,
    'Custom Page Text Template': CustomPageTextTemplate,
    'Email Template': EmailTemplate,
};
const cx = classNames.bind(styles);

const DESCRIPTION_MAX_LENGTH = 150;

const formatDescriptionPreview = (html: string | undefined, maxLength = DESCRIPTION_MAX_LENGTH): string => {
    if (!html || typeof html !== "string") return "";
    const text = html
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    const plain = text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;
    return plain;
};

interface EstimateTemplateProps {
    isAddEstimateTemplate: boolean;
    setIsAddEstimateTemplate: (isAddEstimateTemplate: boolean) => void;
    // onTemplateCreatedView?: (isViewingCreatedPdf: boolean) => void;
}

const EstimateTemplate = ({
    isAddEstimateTemplate,
    setIsAddEstimateTemplate,
    // onTemplateCreatedView
}: EstimateTemplateProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const { estimateTemplates, needRefreshDataEstimateTemplates } = useAppSelector((state) => state.estimateSetting);
    const [showModalCreateEstimateTemplate, setShowModalCreateEstimateTemplate] = useState(false);
    const [formData, setFormData] = useState({
        templateName: '',
        templateType: '',
        location: '',
    });
    const [createdTemplateData, setCreatedTemplateData] = useState({
        templateName: '',
        templateType: '',
        location: '',
    });
    const [error, setError] = useState({
        templateName: '',
        templateType: '',
        location: '',
    });
    const [selectedTemplateType, setSelectedTemplateType] = useState<string>('');

    useEffect(() => {
        setLoading(true);
        dispatch(handleGetEstimateTemplates()).then(() => {
            setLoading(false);
        });
    }, [dispatch, needRefreshDataEstimateTemplates]);

    const validateForm = () => {
        const newError = {
            templateName: '',
            templateType: '',
            location: '',
        };
        if (!formData.templateName) {
            newError.templateName = 'Template Name is required';
        }
        if (!formData.templateType) {
            newError.templateType = 'Template Type is required';
        }
        if (!formData.location) {
            newError.location = 'Location is required';
        }
        setError(newError);
        return Object.values(newError).every((v) => v === '');
    };

    const handleCloseModalCreateEstimateTemplate = () => {
        setShowModalCreateEstimateTemplate(false);
        setFormData({
            templateName: '',
            templateType: '',
            location: '',
        });
        setError({
            templateName: '',
            templateType: '',
            location: '',
        });
    };

    const handleChange = (name: string, value: string) => {
        setFormData({ ...formData, [name]: value });
    };

    const handleCreateEstimateTemplate = () => {
        if (!validateForm()) {
            return;
        }

        setCreatedTemplateData({
            templateName: formData.templateName,
            templateType: formData.templateType,
            location: formData.location,
        });
        setSelectedTemplateType(formData.templateType);
        setIsAddEstimateTemplate(true);
        setShowModalCreateEstimateTemplate(false);
        setFormData({
            templateName: '',
            templateType: '',
            location: '',
        });
        setError({
            templateName: '',
            templateType: '',
            location: '',
        });
    };

    return (
        <div className={cx('estimate-template-container')}>
            {loading ? (
                <div className={cx('loading-container')}>
                    <LoadingMainContent />
                </div>
            ) : (
                <>
                    {isAddEstimateTemplate ? (
                        <div className={cx('add-estimate-template-container')}>
                            {(() => {
                                const CreateStepComponent = selectedTemplateType
                                    ? TEMPLATE_TYPE_COMPONENT_MAP[selectedTemplateType]
                                    : null;
                                return CreateStepComponent
                                    ? (
                                        <CreateStepComponent
                                            templateName={createdTemplateData.templateName}
                                            templateType={createdTemplateData.templateType}
                                            location={createdTemplateData.location}
                                            onCancel={() => {
                                                setIsAddEstimateTemplate(false);
                                                setSelectedTemplateType('');
                                                setCreatedTemplateData({
                                                    templateName: '',
                                                    templateType: '',
                                                    location: '',
                                                });
                                            }}
                                        />
                                    )
                                    : null;
                            })()}
                        </div>
                    ) : (
                        <div className={cx('list-estimate-template-container')}>
                            <div className={cx('header')}>
                                <h3 className={cx('header-title')}>Estimate Templates</h3>
                                <PrimaryButton
                                    title='Add Template'
                                    icon={<PlusIcon className={cx('add-estimate-template-icon')} />}
                                    onClick={() => {
                                        setFormData({
                                            templateName: '',
                                            templateType: '',
                                            location: '',
                                        });
                                        setError({
                                            templateName: '',
                                            templateType: '',
                                            location: '',
                                        });
                                        setShowModalCreateEstimateTemplate(true);
                                    }}
                                    className={cx('add-estimate-template-button')}
                                />
                            </div>
                            <div className={cx('list-estimate-template-content')}>
                                {estimateTemplates.map((estimateTemplate) => (
                                    <div className={cx('estimate-template-item')} key={estimateTemplate.id}>
                                        <h3 className={cx('estimate-template-item-title')}>{estimateTemplate.templateName}</h3>
                                        <p className={cx('estimate-template-item-description')}>{formatDescriptionPreview(estimateTemplate.templateDescription)}</p>
                                        <p className={cx('estimate-template-item-last-updated')}>
                                            Last updated: {estimateTemplate.updatedAt ? dayjs(estimateTemplate.updatedAt).fromNow() : '-'}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <BaseModalV2
                open={showModalCreateEstimateTemplate}
                onClose={handleCloseModalCreateEstimateTemplate}
                title="Create Template"
                width="90%"
                maxWidth="650px"
            >
                <div className={cx('modal-create-estimate-template-content')}>
                    <div className={cx('input-container')}>
                        <label className={cx('input-label')}>Template Name</label>
                        <PrimaryInput
                            name="templateName"
                            placeholder="Template Name"
                            value={formData.templateName}
                            onChange={(e) => handleChange('templateName', e.target.value ?? '')}
                            errorMessage={error.templateName}
                            className={cx('input-field', { error: error.templateName })}
                        />
                    </div>

                    <div className={cx('input-container')}>
                        <label className={cx('input-label')}>Template Type</label>
                        <PrimarySelectInput
                            name="templateType"
                            placeholder="Select Template Type"
                            options={[
                                { label: 'Introduction Template', value: 'Introduction Template' },
                                { label: 'Estimate Details Template', value: 'Estimate Details Template' },
                                { label: 'Signing & Upgrades', value: 'Signing & Upgrades' },
                                { label: 'Custom Page Text Template', value: 'Custom Page Text Template' },
                                { label: 'Email Template', value: 'Email Template' },
                            ]}
                            value={formData.templateType}
                            onChange={(value) => handleChange('templateType', value ?? '')}
                            errorMessage={error.templateType}
                            className={cx('select-input', { error: error.templateType })}
                        />
                    </div>

                    <div className={cx('input-container')}>
                        <label className={cx('input-label')}>Location</label>
                        <PrimarySelectInput
                            name="location"
                            placeholder="Select Location"
                            options={[
                                { label: 'Christian Brother Roofing', value: 'Christian Brother Roofing' },
                            ]}
                            value={formData.location}
                            onChange={(value) => handleChange('location', value ?? '')}
                            errorMessage={error.location}
                            className={cx('select-input', { error: error.location })}
                        />
                    </div>
                </div>

                <div className={cx('modal-actions')}>
                    <PrimaryButton
                        title="Cancel"
                        onClick={handleCloseModalCreateEstimateTemplate}
                        className={cx('cancel-button')}
                    />
                    <PrimaryButton
                        title="Save"
                        onClick={() => handleCreateEstimateTemplate()}
                        className={cx('create-button')}
                    />
                </div>
            </BaseModalV2>

        </div>
    );
};

EstimateTemplate.displayName = "EstimateTemplate";

export default EstimateTemplate;